import moment from 'moment-timezone'
import { PerfilDoCliente, VendasPorTipoDoCliente } from '../models/PainelDoCliente'

const FUSO_DO_BRASIL = 'America/Sao_Paulo'
const FORMATO_DA_DATA_DE_NASCIMENTO = 'YYYY-MM-DD'
const FATOR_DE_ATRASO_DA_RECOMPRA = 1.1
const PERCENTUAL_MINIMO_DO_PRECO_CHEIO = 92
const PERCENTUAL_MINIMO_DO_SENSIVEL_A_PROMO = 75
const FUNDO_DO_PAINEL_NO_TEMA_ESCURO = '#1E1E1E'
const CONTRASTE_MINIMO_DO_TEXTO = 4.5

export const TEXTO_SEM_DADOS = '- Sem dados'

export type TomDoBadge = 'roxo' | 'azul' | 'cinza' | 'laranja' | 'vermelho' | 'verde' | 'ambar'

export type ChaveDoBadge = 'tipo' | 'vip' | 'recompra' | 'precoCheio'

export interface BadgeDoCliente {
	chave: ChaveDoBadge
	texto: string
	tom: TomDoBadge
	icone: string | null
	ajuda: string
}

export interface PosicaoRfvParaExibir {
	sigla: string
	titulo: string
	texto: string
	percentual: number | null
	cor: string
}

export interface CardDeMetrica {
	chave: string
	titulo: string
	valor: string
}

interface TipoDeClienteParaExibir {
	texto: string
	tom: TomDoBadge
	icone: string
}

const CORES_FIXAS_DOS_TONS: Record<Exclude<TomDoBadge, 'roxo'>, string> = {
	azul: '#2196F3',
	cinza: '#757575',
	laranja: '#FB8C00',
	vermelho: '#FF5252',
	verde: '#4CAF50',
	ambar: '#D97706',
}

const TIPOS_DE_CLIENTE: Record<string, TipoDeClienteParaExibir> = {
	'campeoes': { texto: 'Campeões', tom: 'roxo', icone: 'mdi-trophy' },
	'vip': { texto: 'VIP', tom: 'roxo', icone: 'mdi-medal' },
	'leais': { texto: 'Leal', tom: 'roxo', icone: 'mdi-heart' },
	'alto_ticket': { texto: 'Alto Ticket', tom: 'roxo', icone: 'mdi-cash-multiple' },
	'novos': { texto: 'Novos', tom: 'azul', icone: 'mdi-new-box' },
	'em_ascensao': { texto: 'Em Ascensão', tom: 'azul', icone: 'mdi-arrow-top-right' },
	'alto_potencial': { texto: 'Alto Potencial', tom: 'azul', icone: 'mdi-chart-line' },
	'frequentes': { texto: 'Frequente', tom: 'cinza', icone: 'mdi-repeat' },
	'regular': { texto: 'Regular', tom: 'cinza', icone: 'mdi-account' },
	'hibernando': { texto: 'Hibernando', tom: 'laranja', icone: 'mdi-weather-night' },
	'em_queda': { texto: 'Em Queda', tom: 'laranja', icone: 'mdi-arrow-bottom-right' },
	'segunda_compra_pendente': { texto: 'Segunda Compra Pendente', tom: 'laranja', icone: 'mdi-replay' },
	'em_risco': { texto: 'Em Risco', tom: 'vermelho', icone: 'mdi-trending-down' },
	'inativo': { texto: 'Inativo', tom: 'vermelho', icone: 'mdi-clock-outline' },
	'perdido': { texto: 'Perdido', tom: 'vermelho', icone: 'mdi-account-off' },
	'vip_em_risco': { texto: 'VIP em Risco', tom: 'ambar', icone: 'mdi-alert' },
}

const TIPOS_DE_CLIENTE_QUE_JA_SAO_VIP = ['vip', 'vip_em_risco']

const HORAS_DE_COMPRAR_QUE_INDICAM_AGORA = ['muito_alta', 'alta']

const AJUDA_DOS_BADGES: Record<ChaveDoBadge, string> = {
	tipo: 'Tipo de cliente',
	vip: 'Nível de gasto',
	recompra: 'Época de compra',
	precoCheio: 'Perfil de desconto',
}

const COR_DA_FAIXA_BAIXA_DO_RFV = '#94A3B8'
const COR_DA_FAIXA_ALTA_DO_RFV = '#16A34A'
const COR_DA_FAIXA_MEDIA_DO_RFV = '#D97706'

export function valorOuSemDados(valor: string | number | null | undefined): string {
	if (valor === null || valor === undefined) return TEXTO_SEM_DADOS
	if (typeof valor === 'string' && valor.trim() === '') return TEXTO_SEM_DADOS
	return String(valor)
}

export function rotuloComSemDados(rotulo: string): string {
	return `${rotulo} ${TEXTO_SEM_DADOS}`
}

function temValor<T>(valor: T | null | undefined): valor is T {
	return valor !== null && valor !== undefined
}

function interpretarDataDeNascimento(dataDeNascimento: string | null | undefined): moment.Moment | null {
	if (!dataDeNascimento) return null
	const nascimento = moment.tz(dataDeNascimento.substring(0, 10), FORMATO_DA_DATA_DE_NASCIMENTO, true, FUSO_DO_BRASIL)
	return nascimento.isValid() ? nascimento : null
}

export function calcularDiasParaAniversario(
	dataDeNascimento: string | null | undefined,
	hoje: moment.Moment = moment.tz(FUSO_DO_BRASIL),
): number | null {
	const nascimento = interpretarDataDeNascimento(dataDeNascimento)
	if (!nascimento) return null

	const inicioDeHoje = hoje.clone().tz(FUSO_DO_BRASIL).startOf('day')
	let proximoAniversario = nascimento.clone().year(inicioDeHoje.year())
	if (proximoAniversario.isBefore(inicioDeHoje)) {
		proximoAniversario = nascimento.clone().year(inicioDeHoje.year() + 1)
	}

	return proximoAniversario.diff(inicioDeHoje, 'days')
}

export function textoDoAniversario(
	dataDeNascimento: string | null | undefined,
	hoje: moment.Moment = moment.tz(FUSO_DO_BRASIL),
): string {
	const nascimento = interpretarDataDeNascimento(dataDeNascimento)
	const dias = calcularDiasParaAniversario(dataDeNascimento, hoje)
	if (!nascimento || dias === null) return rotuloComSemDados('Aniversário')
	if (dias === 0) return 'Aniversário hoje'

	return `Aniversário em ${dias} ${dias === 1 ? 'dia' : 'dias'} (${nascimento.format('DD/MM')})`
}

export function capitalizarPrimeiraLetra(texto: string): string {
	const limpo = texto.trim().toLowerCase()
	return limpo.charAt(0).toUpperCase() + limpo.slice(1)
}

export function corDoTom(tom: TomDoBadge, corPrimaria: string): string {
	return tom === 'roxo' ? corPrimaria : CORES_FIXAS_DOS_TONS[tom]
}

interface CorRgb {
	vermelho: number
	verde: number
	azul: number
}

function lerCorRgb(cor: string): CorRgb | null {
	const encontrado = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(cor.trim())
	if (!encontrado) return null
	return {
		vermelho: parseInt(encontrado[1], 16),
		verde: parseInt(encontrado[2], 16),
		azul: parseInt(encontrado[3], 16),
	}
}

function luminanciaDoCanal(canal: number): number {
	const normalizado = canal / 255
	return normalizado <= 0.03928 ? normalizado / 12.92 : Math.pow((normalizado + 0.055) / 1.055, 2.4)
}

function luminanciaRelativa(cor: CorRgb): number {
	return 0.2126 * luminanciaDoCanal(cor.vermelho) + 0.7152 * luminanciaDoCanal(cor.verde) + 0.0722 * luminanciaDoCanal(cor.azul)
}

function contraste(luminanciaA: number, luminanciaB: number): number {
	const maior = Math.max(luminanciaA, luminanciaB)
	const menor = Math.min(luminanciaA, luminanciaB)
	return (maior + 0.05) / (menor + 0.05)
}

function clarearCanal(canal: number, fracao: number): number {
	return Math.round(canal + (255 - canal) * fracao)
}

function misturarComBranco(cor: CorRgb, fracao: number): CorRgb {
	return {
		vermelho: clarearCanal(cor.vermelho, fracao),
		verde: clarearCanal(cor.verde, fracao),
		azul: clarearCanal(cor.azul, fracao),
	}
}

function formatarHexadecimal(cor: CorRgb): string {
	const dois = (canal: number) => canal.toString(16).padStart(2, '0')
	return '#' + dois(cor.vermelho) + dois(cor.verde) + dois(cor.azul)
}

export function corLegivelNoTema(cor: string, temaEscuro: boolean): string {
	const original = lerCorRgb(cor)
	const fundo = lerCorRgb(FUNDO_DO_PAINEL_NO_TEMA_ESCURO)
	if (!temaEscuro || !original || !fundo) return cor

	const luminanciaDoFundo = luminanciaRelativa(fundo)
	for (let fracao = 0; fracao < 1; fracao += 0.05) {
		const candidata = misturarComBranco(original, fracao)
		if (contraste(luminanciaRelativa(candidata), luminanciaDoFundo) >= CONTRASTE_MINIMO_DO_TEXTO) return formatarHexadecimal(candidata)
	}
	return '#FFFFFF'
}

function badgeSemDados(chave: ChaveDoBadge, rotulo: string): BadgeDoCliente {
	return { chave, texto: rotuloComSemDados(rotulo), tom: 'cinza', icone: null, ajuda: AJUDA_DOS_BADGES[chave] }
}

function badgeDoTipoDoCliente(perfil: PerfilDoCliente | null): BadgeDoCliente {
	const cluster = perfil?.cluster
	if (!cluster) return badgeSemDados('tipo', 'Tipo do cliente')

	const tipo = TIPOS_DE_CLIENTE[cluster]
	return {
		chave: 'tipo',
		texto: tipo ? tipo.texto : capitalizarPrimeiraLetra(cluster.replace(/_/g, ' ')),
		tom: tipo ? tipo.tom : 'cinza',
		icone: tipo ? tipo.icone : null,
		ajuda: AJUDA_DOS_BADGES.tipo,
	}
}

function badgeDeVip(perfil: PerfilDoCliente | null): BadgeDoCliente | null {
	if (perfil?.cluster && TIPOS_DE_CLIENTE_QUE_JA_SAO_VIP.includes(perfil.cluster)) return null
	if (!temValor(perfil?.vip)) return badgeSemDados('vip', 'VIP')
	if (!perfil?.vip) return null

	return { chave: 'vip', texto: 'VIP', tom: 'roxo', icone: 'mdi-medal', ajuda: AJUDA_DOS_BADGES.vip }
}

function badgeDeRecompra(perfil: PerfilDoCliente | null): BadgeDoCliente | null {
	const recencia = perfil?.recencia
	const ciclo = recencia?.cicloMedianoDias
	if (!temValor(ciclo)) return badgeSemDados('recompra', 'Recompra')

	if (recencia?.horaDeComprar && HORAS_DE_COMPRAR_QUE_INDICAM_AGORA.includes(recencia.horaDeComprar)) {
		return { chave: 'recompra', texto: 'Agora', tom: 'verde', icone: 'mdi-clock-outline', ajuda: AJUDA_DOS_BADGES.recompra }
	}

	const diasSemComprar = recencia?.diasSemComprar
	if (temValor(diasSemComprar) && diasSemComprar > ciclo * FATOR_DE_ATRASO_DA_RECOMPRA) {
		return {
			chave: 'recompra',
			texto: `Atrasada · ${diasSemComprar - ciclo}d`,
			tom: 'laranja',
			icone: 'mdi-clock-alert-outline',
			ajuda: AJUDA_DOS_BADGES.recompra,
		}
	}

	return null
}

function badgeDePrecoCheio(perfil: PerfilDoCliente | null): BadgeDoCliente {
	const percentual = perfil?.fullPricePct
	if (!temValor(percentual)) return badgeSemDados('precoCheio', 'Preço cheio')

	const ajuda = AJUDA_DOS_BADGES.precoCheio
	if (percentual >= PERCENTUAL_MINIMO_DO_PRECO_CHEIO) {
		return { chave: 'precoCheio', texto: 'Preço cheio', tom: 'verde', icone: 'mdi-tag', ajuda }
	}
	if (percentual >= PERCENTUAL_MINIMO_DO_SENSIVEL_A_PROMO) {
		return { chave: 'precoCheio', texto: 'Sensível a promo', tom: 'ambar', icone: 'mdi-tag', ajuda }
	}
	return { chave: 'precoCheio', texto: 'Responde a promo', tom: 'vermelho', icone: 'mdi-tag', ajuda }
}

export function montarBadgesDoCliente(perfil: PerfilDoCliente | null): BadgeDoCliente[] {
	return [
		badgeDoTipoDoCliente(perfil),
		badgeDeVip(perfil),
		badgeDeRecompra(perfil),
		badgeDePrecoCheio(perfil),
	].filter((badge): badge is BadgeDoCliente => badge !== null)
}

export function formatarCanalPreferido(canal: string | null | undefined): string {
	return valorOuSemDados(canal && canal.trim() ? capitalizarPrimeiraLetra(canal) : null)
}

export function formatarPerfilDeCompra(tags: string[] | null | undefined): string {
	const textos = (tags || [])
		.map(tag => capitalizarPrimeiraLetra(tag.replace(/_/g, ' ')))
		.filter(texto => texto !== '')

	return textos.length ? textos.join(' - ') : TEXTO_SEM_DADOS
}

export function textoDaPosicaoRfv(percentil: number | null | undefined): string {
	if (!temValor(percentil)) return TEXTO_SEM_DADOS
	return `Top ${Math.max(1, Math.round(100 - percentil))}%`
}

export function corDaPosicaoRfv(percentil: number | null | undefined, corPrimaria: string): string {
	if (!temValor(percentil)) return COR_DA_FAIXA_BAIXA_DO_RFV
	if (percentil >= 80) return COR_DA_FAIXA_ALTA_DO_RFV
	if (percentil >= 60) return corPrimaria
	if (percentil >= 40) return COR_DA_FAIXA_MEDIA_DO_RFV
	return COR_DA_FAIXA_BAIXA_DO_RFV
}

export function limitarPercentual(percentual: number | null | undefined): number {
	if (!temValor(percentual)) return 0
	return Math.min(100, Math.max(0, percentual))
}

export function montarPosicoesRfv(perfil: PerfilDoCliente | null, corPrimaria: string): PosicaoRfvParaExibir[] {
	const posicao = perfil?.rfvPercentil
	const colunas = [
		{ sigla: 'R', titulo: 'R · Recência', percentil: posicao?.r },
		{ sigla: 'F', titulo: 'F · Frequência', percentil: posicao?.f },
		{ sigla: 'V', titulo: 'V · Valor', percentil: posicao?.m },
	]

	return colunas.map(coluna => ({
		sigla: coluna.sigla,
		titulo: coluna.titulo,
		texto: textoDaPosicaoRfv(coluna.percentil),
		percentual: temValor(coluna.percentil) ? limitarPercentual(coluna.percentil) : null,
		cor: corDaPosicaoRfv(coluna.percentil, corPrimaria),
	}))
}

export function formatarReais(valor: number | null | undefined): string {
	if (!temValor(valor)) return TEXTO_SEM_DADOS
	return `R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatarReaisAPartirDeCentavos(centavos: number | null | undefined): string {
	return formatarReais(temValor(centavos) ? centavos / 100 : null)
}

export function formatarDias(dias: number | null | undefined): string {
	return temValor(dias) ? `${dias}d` : TEXTO_SEM_DADOS
}

function formatarInteiro(valor: number | null | undefined): string {
	return temValor(valor) ? String(valor) : TEXTO_SEM_DADOS
}

function formatarDuasCasas(valor: number | null | undefined): string {
	if (!temValor(valor)) return TEXTO_SEM_DADOS
	return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function textoDoVendedorPredominante(nome: string | null | undefined): string {
	return `Vendedor predominante: ${valorOuSemDados(nome)}`
}

export function montarMetricasPrincipais(perfil: PerfilDoCliente | null): CardDeMetrica[] {
	return [
		{ chave: 'gasto12m', titulo: 'Gasto 12m', valor: formatarReaisAPartirDeCentavos(perfil?.valor12m?.gastoCentavos) },
		{ chave: 'ciclo', titulo: 'Ciclo de compra', valor: formatarDias(perfil?.recencia?.cicloMedianoDias) },
		{ chave: 'semComprar', titulo: 'Sem comprar', valor: formatarDias(perfil?.recencia?.diasSemComprar) },
	]
}

export function montarMetricasPorTipoDeVenda(vendas: VendasPorTipoDoCliente | null | undefined): CardDeMetrica[] {
	return [
		{ chave: 'dev', titulo: 'Dev', valor: formatarReais(vendas?.dev) },
		{ chave: 'demo', titulo: 'Demo', valor: formatarReais(vendas?.demo) },
		{ chave: 'omni', titulo: 'Omni', valor: formatarReais(vendas?.omni) },
		{ chave: 'presencial', titulo: 'Presencial', valor: formatarReais(vendas?.presencial) },
		{ chave: 'link', titulo: 'Link', valor: formatarReais(vendas?.link) },
		{ chave: 'outros', titulo: 'Outros', valor: formatarReais(vendas?.outros) },
	]
}

export function montarMetricasDeAtendimento(vendas: VendasPorTipoDoCliente | null | undefined): CardDeMetrica[] {
	return [
		{ chave: 'vendas', titulo: 'Vendas', valor: formatarInteiro(vendas?.vendas) },
		{ chave: 'tm', titulo: 'TM', valor: formatarReais(vendas?.tm) },
		{ chave: 'pm', titulo: 'PM', valor: formatarReais(vendas?.pm) },
		{ chave: 'pa', titulo: 'PA', valor: formatarDuasCasas(vendas?.pa) },
	]
}
